import type { Program } from '@/types'

const COLS = ['Order', 'Exercise', 'Sets', 'Reps', 'Load', 'Tempo', 'Rest', 'Coaching notes']
const WIDTHS = [8, 34, 7, 9, 20, 9, 9, 80]

/**
 * Download an athlete's program as an Excel file for parents: a plain-English
 * "How to read" tab, then one tab per week with each day's exercises.
 */
export async function exportProgramToExcel(program: Program, athleteName: string) {
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()

  const nw = program.weeks.length
  const nd = Math.max(0, ...program.weeks.map((w) => w.days.length))
  const guide = [
    [`${athleteName} — ${program.name}`],
    [`${nw} week${nw === 1 ? '' : 's'} · ${nd} session${nd === 1 ? '' : 's'} per week. Each week has its own tab.`],
    [],
    ['Order: exercises with the same letter (A1, A2) are done as a pair — one set of each, back and forth, then rest.'],
    ['Reps: "ea" means each side.'],
    ['Load: BW = bodyweight. RPE = effort out of 10 (RPE 6 ≈ 4 reps left in the tank, RPE 7 ≈ 3 left, RPE 8 ≈ 2 left).'],
    ['Tempo: seconds lowering – pause at the bottom – lifting. e.g. 5-0-1 = 5 seconds down, no pause, 1 second up.'],
    ['Rest: rest after completing the pair/group.'],
  ]
  const g = XLSX.utils.aoa_to_sheet(guide)
  g['!cols'] = [{ wch: 110 }]
  XLSX.utils.book_append_sheet(wb, g, 'How to read')

  const used = new Set<string>()
  for (const w of program.weeks) {
    const rows: (string | number | null)[][] = [[`Week ${w.week}${w.phase ? ` · ${w.phase}` : ''}`]]
    if (w.note) rows.push([w.note])
    rows.push([])
    for (const d of w.days) {
      rows.push([d.focus ? `${d.label} — ${d.focus}` : d.label])
      rows.push(COLS)
      for (const ex of d.exercises) {
        const sets = ex.sets ?? null
        rows.push([
          ex.group ?? '',
          ex.name + (ex.is_warmup ? ' (warm-up)' : ''),
          sets,
          ex.reps ?? '',
          ex.load ?? '',
          ex.tempo ?? '',
          ex.rest ?? '',
          ex.notes ?? '',
        ])
      }
      rows.push([])
    }
    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = WIDTHS.map((wch) => ({ wch }))
    // Sheet names must be unique and ≤31 chars.
    let name = `Week ${w.week}`.slice(0, 31)
    for (let n = 2; used.has(name); n++) name = `Week ${w.week} (${n})`.slice(0, 31)
    used.add(name)
    XLSX.utils.book_append_sheet(wb, ws, name)
  }

  const safe = athleteName.replace(/[\\/:*?"<>|]/g, '').trim() || 'Athlete'
  XLSX.writeFile(wb, `${safe} - From Zero Program.xlsx`)
}
